var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { registerAutomotive } from '../../registryCore';
import { createAutoGroup, num, setParts, setState, str, } from '../../helpers';
import { themeFromProps } from '../../themes';
import { autoCenteredText, fitFontSizeToWidth, fitTextX, fluidFont, resolveBounds, textYForBaseline } from '../../layout';
function buildAutomotiveCalendar(app, group, bounds, theme, props) {
    var _a, _b;
    var year = num(props, 'year', new Date().getFullYear());
    var month = num(props, 'month', new Date().getMonth());
    var now = new Date();
    var highlightDay = 'highlightDay' in props
        ? num(props, 'highlightDay', -1)
        : year === now.getFullYear() && month === now.getMonth()
            ? now.getDate()
            : -1;
    var lines = (_a = props.lines) !== null && _a !== void 0 ? _a : ['No events'];
    var eventLine = str(props, 'event', (_b = lines[0]) !== null && _b !== void 0 ? _b : 'No events');
    var pad = bounds.pad;
    var panelW = bounds.innerWidth;
    var panelH = bounds.innerHeight;
    var compact = panelH < 120 || panelW < 160;
    var showEvents = panelH >= 72;
    var eventH = showEvents ? Math.max(compact ? 12 : 16, Math.round(panelH * 0.13)) : 0;
    var headerH = Math.max(compact ? 11 : 14, Math.round(panelH * 0.1));
    var weekdayH = Math.max(compact ? 9 : 11, Math.round(panelH * 0.07));
    var gridTop = headerH + weekdayH + 2;
    var gridH = Math.max(24, panelH - gridTop - (showEvents ? eventH + 4 : 0));
    var first = new Date(year, month, 1);
    var daysInMonth = new Date(year, month + 1, 0).getDate();
    var startDay = first.getDay();
    var numRows = Math.ceil((startDay + daysInMonth) / 7);
    var cell = Math.max(compact ? 9 : 12, Math.min(Math.floor(panelW / 7), Math.floor(gridH / numRows)));
    var gridW = cell * 7;
    var gridX = pad + Math.max(0, (panelW - gridW) / 2);
    group.add(app.roundedRect({
        x: pad,
        y: pad,
        width: panelW,
        height: panelH,
        cornerRadius: Math.min(8, panelH * 0.08),
        fill: '#111827',
        stroke: theme.dialStroke,
        strokeWidth: compact ? 1 : 1.5,
        listening: false,
    }));
    var monthLabel = first.toLocaleString('default', {
        month: compact ? 'short' : 'long',
        year: 'numeric',
    });
    var headerSize = fluidFont(10, bounds, compact ? 7 : 8, 12);
    group.add(app.text({
        text: monthLabel,
        x: pad + fitTextX(monthLabel, headerSize, panelW),
        y: pad + textYForBaseline(headerH / 2, headerSize),
        fontSize: headerSize,
        fontWeight: 'bold',
        fill: theme.text,
        textAlign: 'left',
        listening: false,
    }));
    var weekdaySize = fluidFont(8, bounds, compact ? 6 : 7, 9);
    var weekdays = compact ? ['S', 'M', 'T', 'W', 'T', 'F', 'S'] : ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
    weekdays.forEach(function (label, i) {
        var cx = gridX + i * cell + cell / 2;
        group.add(app.text({
            text: label,
            x: cx,
            y: pad + headerH + textYForBaseline(weekdayH / 2, weekdaySize),
            fontSize: weekdaySize,
            fontWeight: '600',
            fill: theme.textMuted,
            textAlign: 'center',
            metadata: { textBoxWidth: cell, textBoxCenterY: pad + headerH + weekdayH / 2 },
            listening: false,
        }));
    });
    var daySize = Math.max(compact ? 7 : 8, Math.min(11, Math.floor(cell * 0.42)));
    for (var day = 1; day <= daysInMonth; day++) {
        var cellIdx = startDay + day - 1;
        var col = cellIdx % 7;
        var row = Math.floor(cellIdx / 7);
        var cellX = gridX + col * cell;
        var cellY = pad + gridTop + row * cell;
        var isToday = day === highlightDay;
        if (isToday) {
            group.add(app.roundedRect({
                x: cellX + Math.max(1, cell * 0.12),
                y: cellY + Math.max(1, cell * 0.1),
                width: Math.max(6, cell * 0.76),
                height: Math.max(6, cell * 0.8),
                cornerRadius: Math.max(2, cell * 0.18),
                fill: theme.accent,
                listening: false,
            }));
        }
        group.add(app.text({
            text: String(day),
            x: cellX + cell / 2,
            y: cellY + textYForBaseline(cell / 2, daySize),
            fontSize: daySize,
            fontWeight: isToday ? 'bold' : '500',
            fill: isToday ? '#fff' : theme.text,
            textAlign: 'center',
            metadata: { textBoxWidth: cell, textBoxCenterY: cellY + cell / 2 },
            listening: false,
        }));
    }
    if (showEvents) {
        var eventSize = fluidFont(9, bounds, compact ? 6 : 7, 10);
        var eventText = eventLine.length > 28 ? "".concat(eventLine.slice(0, 27), "\u2026") : eventLine;
        var eventY = pad + panelH - eventH / 2;
        group.add(app.text({
            text: eventText,
            x: pad + fitTextX(eventText, eventSize, panelW),
            y: textYForBaseline(eventY, eventSize),
            fontSize: eventSize,
            fill: theme.textMuted,
            textAlign: 'left',
            listening: false,
        }));
    }
}
registerAutomotive('calendar', function (props, app) {
    var _a;
    var theme = themeFromProps(props);
    var bounds = resolveBounds(props, 200, 140);
    var group = createAutoGroup(app, 'calendar', __assign(__assign({}, props), { width: bounds.width, height: bounds.height }), 'calendar');
    buildAutomotiveCalendar(app, group, bounds, theme, props);
    var year = num(props, 'year', new Date().getFullYear());
    var month = num(props, 'month', new Date().getMonth());
    var lines = (_a = props.lines) !== null && _a !== void 0 ? _a : ['No events'];
    group.metadata.linesRefresh = function (next) {
        setState(group, { lines: next });
    };
    setState(group, {
        year: year,
        month: month,
        highlightDay: num(props, 'highlightDay', -1),
        lines: lines,
        width: bounds.width,
        height: bounds.height,
    });
    return group;
});
function callerInitials(name) {
    var _a, _b;
    var parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2)
        return "".concat((_a = parts[0][0]) !== null && _a !== void 0 ? _a : '').concat((_b = parts[1][0]) !== null && _b !== void 0 ? _b : '').toUpperCase();
    return (name.slice(0, 2) || '?').toUpperCase();
}
registerAutomotive('callScreen', function (props, app) {
    var _a, _b;
    var theme = themeFromProps(props);
    var lines = (_a = props.lines) !== null && _a !== void 0 ? _a : ['Incoming…', 'Swipe to answer'];
    var caller = str(props, 'caller', str(props, 'name', 'Alex Morgan'));
    var status = str(props, 'status', 'incoming').toLowerCase();
    var subtitle = str(props, 'subtitle', str(props, 'phone', 'Mobile'));
    var hint = str(props, 'hint', (_b = lines[1]) !== null && _b !== void 0 ? _b : 'Swipe to answer');
    var bounds = resolveBounds(props, 220, 130);
    var group = createAutoGroup(app, 'callScreen', __assign(__assign({}, props), { width: bounds.width, height: bounds.height }), 'callScreen');
    var pad = bounds.pad;
    var panelW = bounds.innerWidth;
    var panelH = bounds.innerHeight;
    var compact = panelH < 88 || panelW < 140;
    var statusLabel = status === 'active' ? 'ON CALL' : status === 'ended' ? 'CALL ENDED' : 'INCOMING CALL';
    group.add(app.roundedRect({
        x: pad,
        y: pad,
        width: panelW,
        height: panelH,
        cornerRadius: Math.min(10, panelH * 0.1),
        fill: '#111827',
        stroke: theme.dialStroke,
        strokeWidth: compact ? 1 : 1.5,
        listening: false,
    }));
    var statusSize = fluidFont(8, bounds, 6, 9);
    group.add(app.text({
        text: statusLabel,
        x: pad + fitTextX(statusLabel, statusSize, panelW),
        y: pad + textYForBaseline((compact ? 10 : 12), statusSize),
        fontSize: statusSize,
        fontWeight: 'bold',
        fill: theme.textMuted,
        textAlign: 'left',
        listening: false,
    }));
    var avatarSize = Math.max(compact ? 26 : 34, Math.min(panelH * 0.36, panelW * 0.2));
    var avatarX = pad + Math.max(4, panelW * 0.03);
    var avatarY = pad + (compact ? 16 : 20);
    group.add(app.circle({
        x: avatarX,
        y: avatarY,
        radius: avatarSize / 2,
        fill: theme.accent,
        opacity: 0.85,
        listening: false,
    }));
    var initials = callerInitials(caller);
    var initialsSize = Math.max(compact ? 9 : 11, Math.floor(avatarSize * 0.3));
    group.add(autoCenteredText(app, initials, avatarSize, avatarSize / 2, {
        fontSize: initialsSize,
        fontWeight: 'bold',
        fill: '#fff',
        insetX: avatarX,
        insetY: avatarY,
    }));
    var textX0 = avatarX + avatarSize + Math.max(6, panelW * 0.03);
    var textW = Math.max(40, pad + panelW + pad - textX0 - 4);
    var callerMax = fluidFont(14, bounds, compact ? 10 : 11, 16);
    var callerFit = fitFontSizeToWidth(caller, textW, callerMax, 8);
    var nameY = avatarY + avatarSize * 0.22;
    var nameText = app.text({
        text: caller,
        x: textX0 + callerFit.x,
        y: textYForBaseline(nameY, callerFit.fontSize),
        fontSize: callerFit.fontSize,
        fontWeight: 'bold',
        fill: theme.text,
        textAlign: 'left',
        listening: false,
    });
    var subSize = fluidFont(9, bounds, 7, 10);
    var subText = app.text({
        text: subtitle,
        x: textX0 + fitTextX(subtitle, subSize, textW),
        y: textYForBaseline(nameY + callerFit.fontSize * 0.95, subSize),
        fontSize: subSize,
        fill: theme.textMuted,
        textAlign: 'left',
        listening: false,
    });
    group.add(nameText, subText);
    var btnH = Math.max(compact ? 18 : 22, Math.min(28, panelH * 0.16));
    var btnGap = Math.max(6, panelW * 0.04);
    var btnW = Math.max(compact ? 44 : 56, (panelW - btnGap) / 2);
    var btnY = pad + panelH - btnH - (compact ? 8 : 10);
    var declineX = pad + Math.max(0, (panelW - btnW * 2 - btnGap) / 2);
    var answerX = declineX + btnW + btnGap;
    group.add(app.roundedRect({
        x: declineX,
        y: btnY,
        width: btnW,
        height: btnH,
        cornerRadius: Math.min(8, btnH / 2),
        fill: theme.warning,
        listening: false,
    }), app.roundedRect({
        x: answerX,
        y: btnY,
        width: btnW,
        height: btnH,
        cornerRadius: Math.min(8, btnH / 2),
        fill: theme.ok,
        listening: false,
    }));
    var btnLabel = compact ? { decline: 'End', answer: 'Ans' } : { decline: 'Decline', answer: 'Answer' };
    var btnFont = fluidFont(9, bounds, compact ? 7 : 8, 10);
    group.add(autoCenteredText(app, btnLabel.decline, btnW, btnH / 2, {
        fontSize: btnFont,
        fontWeight: 'bold',
        fill: '#fff',
        insetX: declineX,
        insetY: btnY,
    }), autoCenteredText(app, btnLabel.answer, btnW, btnH / 2, {
        fontSize: btnFont,
        fontWeight: 'bold',
        fill: '#fff',
        insetX: answerX,
        insetY: btnY,
    }));
    var hintSize = fluidFont(8, bounds, 6, 9);
    var hintText = hint.length > 24 ? "".concat(hint.slice(0, 23), "\u2026") : hint;
    var hintY = Math.max(avatarY + avatarSize + 4, btnY - hintSize - (compact ? 4 : 6));
    var hintNode = app.text({
        text: hintText,
        x: pad + fitTextX(hintText, hintSize, panelW),
        y: textYForBaseline(hintY, hintSize),
        fontSize: hintSize,
        fill: theme.textMuted,
        textAlign: 'left',
        listening: false,
    });
    group.add(hintNode);
    setParts(group, { nameText: nameText, subText: subText, hintNode: hintNode });
    group.metadata.linesRefresh = function (next) {
        if (next[0])
            subText.text = next[0];
        if (next[1]) {
            var nextHint = next[1].length > 24 ? "".concat(next[1].slice(0, 23), "\u2026") : next[1];
            hintNode.text = nextHint;
        }
    };
    group.metadata.refresh = function (nextCaller, nextStatus) {
        var fit = fitFontSizeToWidth(nextCaller, textW, callerMax, 8);
        nameText.text = nextCaller;
        nameText.fontSize = fit.fontSize;
        nameText.x = textX0 + fit.x;
        if (nextStatus)
            setState(group, { status: nextStatus });
    };
    setState(group, { caller: caller, status: status, subtitle: subtitle, hint: hint, lines: lines, width: bounds.width, height: bounds.height });
    return group;
});
